'use client'

import { useState } from 'react'
import Link from 'next/link'
import { motion } from 'framer-motion'

const tiers = [
  {
    name: 'For Humans',
    subtitle: 'Individual access',
    price: 149,
    description: 'Full platform access for collectors, advisors, and dealers',
    featured: false,
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
    features: [
      'Full auction history (50+ years)',
      'AI-powered market analysis',
      'Unlimited artist tracking',
      'Real-time alerts & notifications',
      'Portfolio valuation tools',
      'CSV & PDF exports',
    ],
    cta: 'Request access',
    ctaStyle: 'primary',
  },
  {
    name: 'For Machines',
    subtitle: 'API & MCP access',
    price: null,
    description: 'Programmatic access for AI agents, research systems, and enterprise tools',
    featured: true,
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    features: [
      'Everything in For Humans',
      'Full REST API access',
      'MCP Protocol support',
      'Streaming data feeds',
      'Custom integrations',
      'Multi-seat team access',
      'Dedicated support & SLA',
    ],
    cta: 'Contact us',
    ctaStyle: 'outline',
  },
]

const useCases = [
  {
    title: 'AI Research Agents',
    description: 'Let your AI systems query market data directly via MCP. No scraping, no middleware.',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    title: 'Valuation Systems',
    description: 'Feed real-time comps into your pricing models and insurance platforms.',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    title: 'Portfolio Analytics',
    description: 'Build custom dashboards and reporting tools with live market data.',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    title: 'White-Label Products',
    description: 'Power your client-facing tools with institutional-grade intelligence.',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
  },
]

const faqs = [
  {
    question: 'What is MCP Protocol?',
    answer: 'Model Context Protocol is an open standard that lets AI systems connect directly to data sources. Your AI agents can query our intelligence layer natively, without custom API integration.',
  },
  {
    question: 'Who uses "For Machines" access?',
    answer: 'Auction houses building internal tools, funds running valuation models, insurers automating risk assessment, and anyone building AI-powered art market products.',
  },
  {
    question: 'How do I get started?',
    answer: 'For Humans: request access and we\'ll set up your account within 24 hours. For Machines: contact us for a customized onboarding based on your integration needs.',
  },
  {
    question: 'What makes this different from existing databases?',
    answer: 'Traditional databases give you records. We give you intelligence. Our AI synthesizes 50 years of market history—and every upcoming lot—into insights that matter: pricing confidence, timing signals, comparable analysis before the hammer falls.',
  },
]

function CheckIcon() {
  return (
    <svg className="w-5 h-5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  )
}

export function Pricing() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  return (
    <section id="pricing" className="py-24 px-6 bg-card">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight mb-4">
            Intelligence that pays for itself
          </h2>
          <p className="text-text-secondary max-w-2xl mx-auto text-lg">
            One insight can justify a year of access. Whether you're a human making decisions or a machine powering them.
          </p>
        </div>

        {/* Pricing cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {tiers.map((tier, i) => (
            <motion.div
              key={tier.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              viewport={{ once: true }}
              className={`relative rounded-2xl p-8 flex flex-col ${
                tier.featured
                  ? 'border-2'
                  : 'bg-page border border-border'
              }`}
              style={
                tier.featured
                  ? {
                      background: 'var(--hero-ground)',
                      borderColor: 'var(--accent-primary)',
                    }
                  : {}
              }
            >
              {/* Icon */}
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                style={{
                  background: tier.featured ? 'rgba(255,255,255,0.1)' : 'var(--card)',
                  color: tier.featured ? 'var(--accent-warm)' : 'var(--accent-primary)',
                }}
              >
                {tier.icon}
              </div>

              {/* Tier name & subtitle */}
              <h3
                className="font-display text-2xl font-light mb-1"
                style={{ color: tier.featured ? 'var(--accent-cream)' : 'var(--text-primary)' }}
              >
                {tier.name}
              </h3>
              <p
                className="text-sm mb-4"
                style={{ color: tier.featured ? 'rgba(255,255,255,0.5)' : 'var(--text-tertiary)' }}
              >
                {tier.subtitle}
              </p>

              {/* Price */}
              <div className="mb-4">
                {tier.price ? (
                  <>
                    <span
                      className="text-4xl font-display font-light"
                      style={{ color: tier.featured ? 'var(--accent-cream)' : 'var(--text-primary)' }}
                    >
                      ${tier.price}
                    </span>
                    <span
                      className="text-lg"
                      style={{ color: tier.featured ? 'rgba(255,255,255,0.6)' : 'var(--text-tertiary)' }}
                    >
                      /month
                    </span>
                  </>
                ) : (
                  <span
                    className="text-4xl font-display font-light"
                    style={{ color: tier.featured ? 'var(--accent-cream)' : 'var(--text-primary)' }}
                  >
                    Custom
                  </span>
                )}
              </div>

              {/* Description */}
              <p
                className="text-sm mb-6"
                style={{ color: tier.featured ? 'rgba(255,255,255,0.7)' : 'var(--text-secondary)' }}
              >
                {tier.description}
              </p>

              {/* CTA */}
              <Link
                href="/get-started"
                className="w-full pill mb-6 text-center"
                style={
                  tier.ctaStyle === 'primary'
                    ? {
                        background: 'var(--accent-primary)',
                        color: 'white',
                      }
                    : tier.featured
                    ? {
                        background: 'transparent',
                        border: '1px solid rgba(255,255,255,0.3)',
                        color: 'var(--accent-cream)',
                      }
                    : {
                        background: 'transparent',
                        border: '1px solid var(--border-standard)',
                        color: 'var(--text-primary)',
                      }
                }
              >
                {tier.cta}
              </Link>

              {/* Features */}
              <ul className="space-y-3 flex-1">
                {tier.features.map((feature) => (
                  <li
                    key={feature}
                    className="flex items-start gap-3 text-sm"
                    style={{ color: tier.featured ? 'rgba(255,255,255,0.8)' : 'var(--text-secondary)' }}
                  >
                    <span className="mt-0.5" style={{ color: tier.featured ? 'var(--accent-warm)' : 'var(--positive)' }}>
                      <CheckIcon />
                    </span>
                    {feature}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Capability highlights */}
        <div className="mb-20 max-w-2xl mx-auto">
          <div className="space-y-4 text-center">
            <p className="text-text-secondary">
              <strong className="text-text-primary">Intelligence, not just data</strong> — AI-synthesized insights that tell you what the data means.
            </p>
            <p className="text-text-secondary">
              <strong className="text-text-primary">Agent-ready architecture</strong> — Full MCP Protocol support for your AI systems.
            </p>
            <p className="text-text-secondary">
              <strong className="text-text-primary">Institutional-grade security</strong> — SOC 2 compliant. Your data stays yours.
            </p>
          </div>
        </div>

        {/* Use Cases for Machines */}
        <div className="mb-20">
          <h3 className="font-display text-2xl font-light mb-2 text-center">What machines build with us</h3>
          <p className="text-text-tertiary text-center mb-8 text-sm">Common integrations for API & MCP clients</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {useCases.map((useCase, i) => (
              <motion.div
                key={useCase.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                viewport={{ once: true }}
                className="bg-page rounded-xl border border-border p-5"
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                  style={{ background: 'var(--card)', color: 'var(--accent-primary)' }}
                >
                  {useCase.icon}
                </div>
                <h4 className="font-medium text-sm mb-1">{useCase.title}</h4>
                <p className="text-xs text-text-tertiary">{useCase.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="mb-16">
          <h3 className="font-display text-2xl font-light mb-8 text-center">Questions</h3>
          <div className="max-w-3xl mx-auto">
            {faqs.map((faq, i) => (
              <div key={i} className="border-b border-border">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between py-5 text-left"
                >
                  <span className="text-sm font-medium">{faq.question}</span>
                  <svg
                    className={`w-5 h-5 text-text-tertiary transition-transform ${openFaq === i ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openFaq === i && (
                  <div className="pb-5 text-sm text-text-secondary">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <div
          className="rounded-2xl p-8 text-center"
          style={{ background: 'var(--hero-ground)' }}
        >
          <h3
            className="font-display text-2xl font-light mb-2"
            style={{ color: 'var(--accent-cream)' }}
          >
            Ready to see the market differently?
          </h3>
          <p
            className="text-sm max-w-md mx-auto mb-6"
            style={{ color: 'rgba(255,255,255,0.7)' }}
          >
            Whether you're building or deciding, we have the intelligence layer you need.
          </p>
          <Link
            href="/get-started"
            className="pill inline-flex"
            style={{ background: 'var(--accent-primary)', color: 'white' }}
          >
            Request access
          </Link>
        </div>
      </div>
    </section>
  )
}
